/** Журнал кліків і помилок. Секрети не записуємо. */

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api'
const STORAGE_KEY = 'p2p-activity-queue'
const MAX_QUEUE = 80

export type ActivityKind = 'click' | 'navigate' | 'submit' | 'api_error' | 'js_error'

export interface ActivityEvent {
  kind: ActivityKind
  path: string
  label?: string | null
  href?: string | null
  method?: string | null
  status?: number | null
  message?: string | null
  request_id?: string | null
  occurred_at: string
}

let queue: ActivityEvent[] = loadQueue()
let timer: number | null = null
let flushing = false

function loadQueue(): ActivityEvent[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as ActivityEvent[]
    return Array.isArray(parsed) ? parsed.slice(-MAX_QUEUE) : []
  } catch {
    return []
  }
}

function persist() {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(queue.slice(-MAX_QUEUE)))
  } catch {
    // quota / private mode
  }
}

function clip(text: string, max = 160): string {
  const compact = text.replace(/\s+/g, ' ').trim()
  return compact.length > max ? `${compact.slice(0, max - 1)}…` : compact
}

function isSecretField(el: Element): boolean {
  if (!(el instanceof HTMLInputElement) && !(el instanceof HTMLTextAreaElement)) return false
  const type = el instanceof HTMLInputElement ? el.type : 'text'
  const blob = `${type} ${el.name} ${el.id} ${el.autocomplete} ${el.placeholder}`.toLowerCase()
  return (
    type === 'password' ||
    blob.includes('password') ||
    blob.includes('secret') ||
    blob.includes('token') ||
    blob.includes('api_key') ||
    blob.includes('api-key')
  )
}

function labelFromElement(el: Element | null): string {
  if (!el) return 'екран'
  if (isSecretField(el)) return 'секретне поле'
  const labelled = el.closest('[aria-label]')
  const aria = labelled?.getAttribute('aria-label')
  if (aria) return clip(aria)
  if (el instanceof HTMLElement) {
    const text = clip(el.innerText || el.textContent || '')
    if (text && text.length <= 160) return text
  }
  const tag = el.tagName.toLowerCase()
  const name = el instanceof HTMLInputElement ? el.type : tag
  return name
}

export function recordActivity(partial: Omit<ActivityEvent, 'occurred_at' | 'path'> & { path?: string }): void {
  if (partial.kind === 'api_error' && (partial.href || '').includes('/activity/events')) return
  const event: ActivityEvent = {
    kind: partial.kind,
    path: clip(partial.path ?? (typeof location !== 'undefined' ? location.pathname : '/'), 240),
    label: partial.label ? clip(partial.label) : null,
    href: partial.href ? clip(partial.href, 240) : null,
    method: partial.method ?? null,
    status: partial.status ?? null,
    message: partial.message ? clip(partial.message, 400) : null,
    request_id: partial.request_id ?? null,
    occurred_at: new Date().toISOString(),
  }
  queue.push(event)
  if (queue.length > MAX_QUEUE) queue = queue.slice(-MAX_QUEUE)
  persist()
  if (queue.length >= 8) void flushActivity()
  else scheduleFlush()
}

function scheduleFlush() {
  if (timer != null) return
  timer = window.setTimeout(() => {
    timer = null
    void flushActivity()
  }, 4000)
}

export async function flushActivity(): Promise<void> {
  if (flushing || queue.length === 0) return
  if (typeof location !== 'undefined' && location.pathname === '/login') return
  const batch = queue.splice(0, 50)
  persist()
  flushing = true
  try {
    const response = await fetch(`${BASE_URL}/v1/activity/events`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ events: batch }),
      keepalive: true,
    })
    if (!response.ok && response.status !== 401) {
      queue = [...batch, ...queue].slice(-MAX_QUEUE)
      persist()
    } else if (!response.ok) {
      queue = [...batch, ...queue].slice(-MAX_QUEUE)
      persist()
    }
  } catch {
    queue = [...batch, ...queue].slice(-MAX_QUEUE)
    persist()
  } finally {
    flushing = false
  }
}

export function startActivityCapture(): () => void {
  const onClick = (event: MouseEvent) => {
    const target = event.target
    if (!(target instanceof Element)) return
    const interactive = target.closest('a, button, [role="button"], summary, label, select, input[type="checkbox"], input[type="radio"]')
    if (!interactive) return
    const href = interactive instanceof HTMLAnchorElement ? interactive.getAttribute('href') : null
    recordActivity({
      kind: 'click',
      label: labelFromElement(interactive),
      href,
    })
  }
  const onSubmit = (event: Event) => {
    const form = event.target
    if (!(form instanceof HTMLFormElement)) return
    recordActivity({
      kind: 'submit',
      label: form.getAttribute('aria-label') || form.id || 'форма',
    })
  }
  const onError = (event: ErrorEvent) => {
    recordActivity({
      kind: 'js_error',
      message: clip(event.message || 'js error', 400),
      label: event.filename ? clip(event.filename, 160) : null,
    })
  }
  const onReject = (event: PromiseRejectionEvent) => {
    const reason = event.reason
    const message = reason instanceof Error ? reason.message : String(reason ?? 'unhandledrejection')
    recordActivity({ kind: 'js_error', message: clip(message, 400), label: 'unhandledrejection' })
  }
  const onHide = () => {
    void flushActivity()
  }
  document.addEventListener('click', onClick, true)
  document.addEventListener('submit', onSubmit, true)
  window.addEventListener('error', onError)
  window.addEventListener('unhandledrejection', onReject)
  document.addEventListener('visibilitychange', onHide)
  window.addEventListener('pagehide', onHide)
  return () => {
    document.removeEventListener('click', onClick, true)
    document.removeEventListener('submit', onSubmit, true)
    window.removeEventListener('error', onError)
    window.removeEventListener('unhandledrejection', onReject)
    document.removeEventListener('visibilitychange', onHide)
    window.removeEventListener('pagehide', onHide)
  }
}
